import { Check, X } from "lucide-react";
import { motion, useMotionValue, useTransform } from "motion/react";
import { animate } from "motion";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import type { Doc } from "@convex/_generated/dataModel";

interface ReviewCardProps {
  currentCard: Doc<"cards">;
  handleResponse: (remembered: boolean) => void;
}

export const ReviewCard = ({ currentCard, handleResponse }: ReviewCardProps) => {
  const [showAnswer, setShowAnswer] = useState<boolean>(false);
  const [isAnimatingOut, setIsAnimatingOut] = useState<boolean>(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter" && !showAnswer) {
        setShowAnswer(true);
      } else if (showAnswer) {
        if (e.key === "f" || e.key === "F") {
          handleResponse(false);
        } else if (e.key === "j" || e.key === "J") {
          handleResponse(true);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [showAnswer, handleResponse]);

  const rememberedOrForgotButtons = (
    <div className="grid grid-cols-2 gap-4 p-4">
      <Button onClick={() => handleResponse(false)} variant="destructive">
        Forgot
        <span className="ml-2 text-xs border px-1 rounded hidden md:block">F</span>
      </Button>
      <Button onClick={() => handleResponse(true)}>
        Remembered
        <span className="ml-2 text-xs border px-1 rounded hidden md:block">J</span>
      </Button>
    </div>
  );

  // Motion values for tracking drag position and derived values
  const x = useMotionValue(0);
  const dragThreshold = 100; // Threshold for considering a card "swiped"

  // Transform x position to rotation (rotate around bottom axis)
  const rotate = useTransform(x, [-dragThreshold * 1.5, 0, dragThreshold * 1.5], [-15, 0, 15]);

  // Transform x position to opacity for indicators
  const forgotOpacity = useTransform(x, [-dragThreshold, -dragThreshold / 2, 0], [1, 0.5, 0]);
  const rememberedOpacity = useTransform(x, [0, dragThreshold / 2, dragThreshold], [0, 0.5, 1]);

  // Transform x position to background colors (border outlines)
  const forgotBorderOpacity = useTransform(
    x,
    [-dragThreshold, -dragThreshold / 2, 0],
    [0.8, 0.4, 0]
  );
  const rememberedBorderOpacity = useTransform(
    x,
    [0, dragThreshold / 2, dragThreshold],
    [0, 0.4, 0.8]
  );
  // Only enable drag functionality when showing the answer
  const canDrag = (currentCard.singleSided ?? false) || showAnswer;
  // Handle drag end - if past threshold, trigger appropriate action
  const handleDragEnd = () => {
    if (!canDrag || isAnimatingOut) return;

    const xValue = x.get();
    const passedLeft = xValue < -dragThreshold;
    const passedRight = xValue > dragThreshold;

    if (!passedLeft && !passedRight) {
      // Animate back to center for a nicer feel
      const velocity = Math.abs(x.getVelocity());
      const distance = Math.abs(xValue);
      const minSpeed = 800; // px/s
      const speed = Math.max(velocity, minSpeed);
      const duration = Math.min(0.35, distance / speed);
      animate(x, 0, { duration, ease: "easeOut" });
      return;
    }

    // Animate off-screen with accelerating finish, then trigger response
    setIsAnimatingOut(true);
    const direction = passedRight ? 1 : -1;
    const viewportWidth = typeof window !== "undefined" ? window.innerWidth : 1000;
    const targetX = direction * (viewportWidth + 200);
    const velocity = Math.abs(x.getVelocity());
    const minSpeed = 1800; // faster baseline so long throws finish quicker
    const speed = Math.max(velocity, minSpeed);
    const distance = Math.abs(targetX - xValue);
    // Keep very fast swipes snappy, but also cap long throws so they don't feel sluggish
    const minDuration = 0.14;
    const maxDuration = 0.5;
    const duration = Math.min(maxDuration, Math.max(minDuration, distance / speed));

    // Use an accelerating curve so it speeds up towards the end
    const controller = animate(x, targetX, { duration, ease: "easeOut" });
    void controller.finished.then(() => {
      handleResponse(passedRight); // right = remembered, left = forgot
    });
  };
  return (
    <motion.div
      key={currentCard._id} // This ensures the animation triggers when the card changes
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4, ease: "easeInOut" }}
      className="flex justify-center"
    >
      <motion.div
        style={{
          x,
          rotate,
          transformOrigin: "bottom center",
          position: "relative",
          width: "100%",
          maxWidth: "32rem", // equivalent to max-w-lg
        }}
        drag={canDrag && !isAnimatingOut ? "x" : false}
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={0.7}
        onDragEnd={handleDragEnd}
        className="w-full max-w-lg"
      >
        {/* Forgot indicator */}
        <motion.div
          style={{ opacity: forgotOpacity }}
          className="absolute top-4 right-4 z-10 font-bold text-destructive transform text-xl"
        >
          <X />
        </motion.div>

        {/* Remembered indicator */}
        <motion.div
          style={{ opacity: rememberedOpacity }}
          className="absolute top-4 left-4 z-10 font-bold text-primary transform text-xl"
        >
          <Check />
        </motion.div>

        {/* Border overlay for forgot (red) */}
        <motion.div
          style={{
            opacity: forgotBorderOpacity,
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            borderWidth: "3px",
            borderStyle: "solid",
            borderColor: "var(--destructive)",
            borderRadius: "0.375rem",
            pointerEvents: "none",
            zIndex: 5,
          }}
        />

        {/* Border overlay for remembered (green) */}
        <motion.div
          style={{
            opacity: rememberedBorderOpacity,
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            borderWidth: "3px",
            borderStyle: "solid",
            borderColor: "var(--primary)",
            borderRadius: "0.375rem",
            pointerEvents: "none",
            zIndex: 5,
          }}
        />
        <Card className="w-full shadow-lg">
          <CardContent className="p-0">
            <div className="p-8 border-b min-h-[200px] flex items-center justify-center">
              <div className="text-center text-lg">{currentCard.question}</div>
            </div>
            {currentCard.singleSided ? (
              rememberedOrForgotButtons
            ) : showAnswer ? (
              <div>
                <div className="p-8 min-h-[200px] flex items-center justify-center border-b">
                  <div className="text-center whitespace-pre-line">{currentCard.answer}</div>
                </div>
                {rememberedOrForgotButtons}
              </div>
            ) : (
              <div className="p-8 min-h-[200px] flex items-center justify-center bg-gray-100">
                <div className="mb-4">
                  <Button variant="muted" onClick={() => setShowAnswer(true)}>
                    Reveal Answer
                    <span className="bg-white/20 text-white text-xs ml-2 px-2 py-0.5 rounded hidden md:block">
                      ENTER
                    </span>
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </motion.div>
  );
};
